/**
 * Monitoring Page - View people I'm monitoring
 *
 * Shows list of diabetics the current user is monitoring.
 * When clicking on a user, shows their full history using history-page with an adapter.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  relationshipService,
  type MonitoredUser,
} from "../services/relationships";
import { authService } from "../services/auth";
import { MonitoredUserAdapter } from "../services/history-data-adapter";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "../components/app-shell";
import "./history-page";

@customElement("monitoring-page")
export class MonitoringPage extends LitElement {
  @state() private monitoredUsers: MonitoredUser[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private loadingUserId: string | null = null;
  @state() private viewedUserAdapter: MonitoredUserAdapter | null = null;

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 1.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    .subtitle {
      color: #6c757d;
      font-size: 0.875rem;
      margin-top: -1rem;
      margin-bottom: 1.5rem;
    }

    .users-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }

    .user-card {
      background: #f8f9fa;
      border-radius: 0.75rem;
      padding: 1.25rem;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 2px solid transparent;
    }

    .user-card:hover {
      border-color: #007bff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .user-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.75rem;
    }

    .user-name {
      font-weight: 600;
      font-size: 1.125rem;
      color: #333;
    }

    .user-email {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    .user-stats {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid #e9ecef;
    }

    .stat {
      text-align: center;
    }

    .stat-value {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    .last-sync {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.75rem;
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
      color: #6c757d;
    }

    .empty-state sl-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: #dee2e6;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .stop-monitoring-btn {
      margin-top: 0.5rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.loadMonitoredUsers();
  }

  private async loadMonitoredUsers() {
    this.isLoading = true;
    this.error = "";

    try {
      this.monitoredUsers = await relationshipService.getMonitoredUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load monitored users";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    // If viewing a specific user, show history-page with their data adapter
    if (this.viewedUserAdapter) {
      return html`<history-page
        .dataAdapter=${this.viewedUserAdapter}
      ></history-page>`;
    }

    const authState = authService.state;
    const isMonitor = authState.roles?.includes("monitor");
    const isClinician = authState.roles?.includes("clinician");
    const pageTitle = isClinician && !isMonitor ? "My Patients" : "Monitoring";

    return html`
      <app-shell pageTitle=${pageTitle}>
        <div class="page-content">
          <h1>
            ${isClinician && !isMonitor ? "My Patients" : "People I Monitor"}
          </h1>
          <p class="subtitle">
            ${isClinician && !isMonitor
              ? "View your patients' glucose data on demand"
              : "View real-time glucose data from people who share with you"}
          </p>

          ${this.error
            ? html`<sl-alert variant="danger" open>${this.error}</sl-alert>`
            : ""}
          ${this.isLoading
            ? this.renderLoading()
            : this.monitoredUsers.length === 0
              ? this.renderEmptyState()
              : this.renderUsersGrid()}
        </div>
      </app-shell>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <sl-spinner style="font-size: 2rem;"></sl-spinner>
      </div>
    `;
  }

  private renderEmptyState() {
    const isClinician = authService.state.roles?.includes("clinician");
    return html`
      <div class="empty-state">
        <sl-icon name="people"></sl-icon>
        <h3>No one to monitor yet</h3>
        <p>
          ${isClinician
            ? "When patients share their data with you, they'll appear here."
            : "When friends or family share their data with you, they'll appear here."}
        </p>
        <p>Ask them to send you an invite link from their Share page.</p>
      </div>
    `;
  }

  private renderUsersGrid() {
    return html`
      <div class="users-grid">
        ${this.monitoredUsers.map((user) => this.renderUserCard(user))}
      </div>
    `;
  }

  private renderUserCard(user: MonitoredUser) {
    const lastSync = user.lastSyncedAt
      ? new Date(user.lastSyncedAt).toLocaleString()
      : "Never";
    const isLoading = this.loadingUserId === user.userId;

    return html`
      <div class="user-card" @click=${() => this.viewUser(user)}>
        <div class="user-header">
          <div>
            <div class="user-name">${user.displayName}</div>
            ${user.email
              ? html`<div class="user-email">${user.email}</div>`
              : ""}
          </div>
          <sl-badge
            variant="${user.relationshipType === "monitor"
              ? "primary"
              : "neutral"}"
          >
            ${user.relationshipType === "monitor" ? "Family/Friend" : "Patient"}
          </sl-badge>
        </div>

        ${user.lastGlucose
          ? html`
              <div class="user-stats">
                <div class="stat">
                  <div class="stat-value">${user.lastGlucose}</div>
                  <div class="stat-label">Last Glucose</div>
                </div>
                <div class="stat">
                  <div class="stat-value">${user.entriesCount || 0}</div>
                  <div class="stat-label">Entries</div>
                </div>
              </div>
            `
          : html`
              <div class="last-sync">
                ${isLoading
                  ? html`<sl-spinner></sl-spinner> Loading...`
                  : "Click to view data"}
              </div>
            `}

        <div class="last-sync">Last viewed: ${lastSync}</div>

        <sl-button
          class="stop-monitoring-btn"
          size="small"
          variant="text"
          @click=${(e: Event) => this.stopMonitoring(e, user)}
        >
          <sl-icon slot="prefix" name="x-circle"></sl-icon>
          Stop Monitoring
        </sl-button>
      </div>
    `;
  }

  private viewUser(user: MonitoredUser) {
    // Create adapter with back button handler
    this.viewedUserAdapter = new MonitoredUserAdapter(
      user.userId,
      user.displayName,
      user.email,
      () => this.backToList(),
    );
  }

  private backToList() {
    this.viewedUserAdapter = null;
  }

  private async stopMonitoring(e: Event, user: MonitoredUser) {
    e.stopPropagation(); // Prevent card click

    if (
      !confirm(
        `Stop monitoring ${user.displayName}? You will no longer be able to view their data.`,
      )
    ) {
      return;
    }

    try {
      await relationshipService.stopMonitoring(user.userId);
      await this.loadMonitoredUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to stop monitoring";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "monitoring-page": MonitoringPage;
  }
}
